import React, {Component} from "react"
import PropTypes from "prop-types"
import {Col} from "react-bootstrap"
import {omit, isString, isArray, xor, isEmpty} from "lodash"
import {SelectComponentsConfig} from "react-select"
import AsyncPaginate from "react-select-async-paginate"

import ValidationInput, {ValidationProps} from "@irati/utils/validation/Input"
import {InjectIntl, injectIntl, getResolvedMessage} from "@irati/utils/lang"
import newId from "@irati/utils/id"

import {FormGroup} from "../Form"
import ErrorText from "../ErrorText"
import HintText from "../HintText"

import {SelectOption, SelectValueType as Value} from "./Select"
import perkboxStyles from "./styles/selectStyles"

interface Props extends InjectIntl, ValidationProps {
  className?: string,
  id?: string,
  hideLabel?: boolean,
  hint?: string,
  label?: string,
  value?: Value,
  labelKey?: string,
  valueKey?: string,
  components?: SelectComponentsConfig<SelectOption>,
  name: string,
  onChange: (value: Value) => void
}

interface State {
  value?: Value,
  blurred: boolean,
  errorMsg: string | null
}

export class FormItemSelectInfiniteAsync extends Component<Props, State> {
  validation: ValidationInput

  static contextTypes = {
    currentScreen: PropTypes.string
  }

  static defaultProps: Omit<Props, "intl" | "formatMessage" | "name" | "onChange"> = {
    className: "",
    id: "",
    hideLabel: false,
    valueKey: "value",
    labelKey: "label"
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const {value: propsValue} = props
    const {value: stateValue} = state

    if (
      (isString(propsValue) && propsValue !== stateValue) ||
      isEmpty(stateValue) ||
      (isArray(propsValue) && (isArray(stateValue) && xor(propsValue, stateValue).length > 0))
    ) {
      return {
        value: propsValue
      }
    }
    return null
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      errorMsg: null,
      blurred: false,
      value: props.value
    }

    this.validation = new ValidationInput(this)
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    this.validation.update(prevProps, prevState)
  }

  componentWillUnmount() {
    this.validation.destroy()
  }

  handleBlur = () => {
    this.validation.update()
  }

  handleChange = (value: Value) => {
    this.setState({value})
    this.props.onChange(value)
  }

  render() {
    const {name, label, className, hint, hideLabel, labelKey, valueKey} = this.props
    const {errorMsg, value} = this.state
    const idNamespace = newId("select-infinite-async", this.props)
    const formItemId = newId("form-input", idNamespace)

    const selectProps = omit(this.props, ["className", "label", "validation", "formatMessage", "translate"])

    return (
      <FormGroup id={idNamespace} className={className} controlId={name} validationState={errorMsg ? "error" : undefined}>
        {
          <Col hidden={hideLabel} xs={12}>
            <label id={newId("label", idNamespace)} htmlFor={formItemId}>
              {label}
            </label>
          </Col>
        }
        <Col xs={12}>
          <AsyncPaginate
            {...selectProps}
            id={formItemId}
            name={name}
            value={value}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            getOptionLabel={labelKey ? (option) => option[labelKey] : undefined}
            getOptionValue={valueKey ? (option) => option[valueKey] : undefined}
            shouldLoadMore={(scrollHeight: number, clientHeight: number, scrollTop: number) =>
              scrollTop > 0 && clientHeight + scrollTop + 50 > scrollHeight
            }
            styles={perkboxStyles} />
          {errorMsg && (
            <div>
              <ErrorText small>{getResolvedMessage(this, "errorMsg", this.state)}</ErrorText>
            </div>
          )}
          {hint && (
            <div>
              <HintText small>{hint}</HintText>
            </div>
          )}
        </Col>
      </FormGroup>
    )
  }
}

export default injectIntl(FormItemSelectInfiniteAsync)
